import React, { useRef } from "react";
import {
  Paper,
  Text,
  Group,
  Stack,
  ActionIcon,
  Anchor,
  UnstyledButton,
} from "@mantine/core";
import { IconUpload, IconTrash, IconFile } from "@tabler/icons";
import moment from "moment";
import { showNotification } from "@mantine/notifications";

const formatSize = (bytes) => {
  if (!bytes) return "--";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getDocumentUrl = (doc) => {
  if (doc?.fileUrl) return doc.fileUrl;
  if (doc?.fileLink) {
    const base = process.env.REACT_APP_FILE_URL ?? "";
    return `${base}${doc.fileLink}`;
  }
  return null;
};

const QuizDocumentUploader = ({
  documents = [],
  onUpload,
  onDelete,
  onOpen,
  readOnly,
}) => {
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = [
      "application/pdf",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "video/mp4",
    ];
    if (file.type && !allowed.includes(file.type)) {
      showNotification({
        title: "Invalid file type",
        message: "Allowed: PDF, PPT, DOC, MP4",
        color: "red",
      });
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      showNotification({
        title: "File too large",
        message: "Maximum file size is 500MB",
        color: "red",
      });
      return;
    }
    await onUpload?.(file);
  };

  const handleOpen = async (doc) => {
    const url = getDocumentUrl(doc);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    await onOpen?.(doc);
  };

  return (
    <Stack spacing="md">
      <Text size="sm" color="dimmed">
        Upload required reading materials. Participants must acknowledge each
        document before taking the quiz.
      </Text>

      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept=".pdf,.ppt,.pptx,.doc,.docx,.mp4"
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <Paper
            p="xl"
            withBorder
            radius="md"
            sx={{
              borderStyle: "dashed",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => inputRef.current?.click()}
          >
            <IconUpload size={32} stroke={1.2} />
            <Text mt="sm">Drag & drop or click to upload</Text>
            <Text size="xs" color="dimmed">
              PDF, PPT, DOC, MP4 — max 500MB
            </Text>
          </Paper>
        </>
      )}

      {!documents.length && (
        <Text size="sm" color="dimmed">
          No documents uploaded yet.
        </Text>
      )}

      {documents.map((doc) => (
        <Paper key={doc._id ?? doc.fileName} p="md" withBorder radius="md">
          <Group position="apart" noWrap>
            <Group spacing="sm" noWrap sx={{ flex: 1, minWidth: 0 }}>
              <IconFile size={20} />
              <div style={{ minWidth: 0 }}>
                <UnstyledButton onClick={() => handleOpen(doc)}>
                  <Anchor
                    component="span"
                    weight={500}
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {doc.fileName ?? "Untitled file"}
                  </Anchor>
                </UnstyledButton>
                <Text size="xs" color="dimmed">
                  {formatSize(doc.fileSize)} ·{" "}
                  {doc.uploadedAt || doc.createdAt
                    ? moment(doc.uploadedAt ?? doc.createdAt).format("MMM D, YYYY")
                    : "--"}
                </Text>
              </div>
            </Group>
            {!readOnly && (
              <ActionIcon onClick={() => onDelete?.(doc._id)}>
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        </Paper>
      ))}
    </Stack>
  );
};

export default QuizDocumentUploader;
